import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useEffect, useState, lazy, Suspense } from "react";
import App from "../App";
import ProtectedAdminRoute from "./ProtectedAdminRoute";
import NotFound from "../pages/NotFound";
import LanguageWrapper from "./LanguageWrapper";

// Lazy load service pages
const Services = lazy(() => import("../pages/Services"));
const RoofRepairs = lazy(() => import("../pages/services/RoofRepairs"));
const RoofMaintenance = lazy(() => import("../pages/services/RoofMaintenance"));
const RoofReplacement = lazy(() => import("../pages/services/RoofReplacement"));
const SkylightsVentilation = lazy(() => import("../pages/services/SkylightsVentilation"));
const NewConstruction = lazy(() => import("../pages/services/NewConstruction"));

// Lazy load location pages
const HoustonRoofing = lazy(() => import("../pages/locations/HoustonRoofing"));
const AtascocitaRoofing = lazy(() => import("../pages/locations/AtascocitaRoofing"));
const WaldenLakeHouston = lazy(() => import("../pages/locations/WaldenLakeHouston"));
const TheWoodlandsRoofing = lazy(() => import("../pages/locations/TheWoodlandsRoofing"));
const KingwoodRoofing = lazy(() => import("../pages/locations/KingwoodRoofing"));
const HumbleRoofing = lazy(() => import("../pages/locations/HumbleRoofing"));
const SpringRoofing = lazy(() => import("../pages/locations/SpringRoofing"));
const ConroeRoofing = lazy(() => import("../pages/locations/ConroeRoofing"));
const AldineRoofing = lazy(() => import("../pages/locations/AldineRoofing"));

// Lazy load about and blog pages
const About = lazy(() => import("../pages/About"));
const BookInspection = lazy(() => import("../pages/BookInspection"));
const Blog = lazy(() => import("../pages/Blog"));
const BlogPost = lazy(() => import("../pages/BlogPost"));
const BlogCategory = lazy(() => import("../pages/BlogCategory"));

// Lazy load admin pages
const AdminLogin = lazy(() => import("../pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("../pages/admin/AdminDashboard"));
const Leads = lazy(() => import("../pages/admin/Leads"));
const BlogPosts = lazy(() => import("../pages/admin/blog/BlogPosts"));
const BlogPostEditor = lazy(() => import("../pages/admin/blog/BlogPostEditor"));
const BlogPostPreview = lazy(() => import("../pages/admin/blog/BlogPostPreview"));
const Categories = lazy(() => import("../pages/admin/blog/Categories"));
const Tags = lazy(() => import("../pages/admin/blog/Tags"));
const Users = lazy(() => import("../pages/admin/Users"));
const Images = lazy(() => import("../pages/admin/Images"));
const Projects = lazy(() => import("../pages/admin/Projects"));
const ProjectEditor = lazy(() => import("../pages/admin/ProjectEditor"));

// Simplified page transition - only opacity to avoid conflicts with content animations
const pageVariants: Variants = {
  initial: {
    opacity: 0,
  },
  enter: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

// Check for reduced motion preference
const getReducedMotionVariants = (): Variants => ({
  initial: { opacity: 0 },
  enter: {
    opacity: 1,
    transition: { duration: 0.15 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.1 },
  },
});

interface PageWrapperProps {
  children: React.ReactNode;
}

const PageWrapper = ({ children }: PageWrapperProps) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const variants = prefersReducedMotion ? getReducedMotionVariants() : pageVariants;

  return (
    <motion.div
      initial="initial"
      animate="enter"
      exit="exit"
      variants={variants}
      style={{ willChange: "opacity" }}
    >
      {children}
    </motion.div>
  );
};

// Minimal loading fallback
const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <LanguageWrapper>
      <AnimatePresence mode="wait">
        <Suspense fallback={<PageLoading />}>
          <Routes location={location} key={location.pathname}>
            {/* English routes (default) */}
            <Route path="/" element={<PageWrapper><App /></PageWrapper>} />
            <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
            <Route path="/book-inspection" element={<PageWrapper><BookInspection /></PageWrapper>} />

            {/* Spanish routes */}
            <Route path="/es" element={<PageWrapper><App /></PageWrapper>} />
            <Route path="/es/about" element={<PageWrapper><About /></PageWrapper>} />
            <Route path="/es/book-inspection" element={<PageWrapper><BookInspection /></PageWrapper>} />
            <Route path="/es/services" element={<PageWrapper><Services /></PageWrapper>} />
            <Route path="/es/services/roof-repairs" element={<PageWrapper><RoofRepairs /></PageWrapper>} />
            <Route path="/es/services/roof-replacement" element={<PageWrapper><RoofReplacement /></PageWrapper>} />
            <Route path="/es/services/roof-maintenance" element={<PageWrapper><RoofMaintenance /></PageWrapper>} />
            <Route path="/es/services/skylights-ventilation" element={<PageWrapper><SkylightsVentilation /></PageWrapper>} />
            <Route path="/es/services/new-construction" element={<PageWrapper><NewConstruction /></PageWrapper>} />
            {/* Spanish Location Pages */}
            <Route path="/es/houston-roofing" element={<PageWrapper><HoustonRoofing /></PageWrapper>} />
            <Route path="/es/roofing-the-woodlands-tx" element={<PageWrapper><TheWoodlandsRoofing /></PageWrapper>} />
            <Route path="/es/roofing-kingwood-tx" element={<PageWrapper><KingwoodRoofing /></PageWrapper>} />
            <Route path="/es/roofing-atascocita-tx" element={<PageWrapper><AtascocitaRoofing /></PageWrapper>} />
            <Route path="/es/roofing-humble-tx" element={<PageWrapper><HumbleRoofing /></PageWrapper>} />
            <Route path="/es/roofing-spring-tx" element={<PageWrapper><SpringRoofing /></PageWrapper>} />
            <Route path="/es/roofing-conroe-tx" element={<PageWrapper><ConroeRoofing /></PageWrapper>} />
            <Route path="/es/roofing-aldine-tx" element={<PageWrapper><AldineRoofing /></PageWrapper>} />
            <Route path="/es/roofing-walden-lake-houston-atascocita-tx" element={<PageWrapper><WaldenLakeHouston /></PageWrapper>} />
          <Route path="/services" element={<PageWrapper><Services /></PageWrapper>} />
          <Route path="/services/roof-repairs" element={<PageWrapper><RoofRepairs /></PageWrapper>} />
          <Route path="/services/roof-maintenance" element={<PageWrapper><RoofMaintenance /></PageWrapper>} />
          <Route path="/services/roof-replacement" element={<PageWrapper><RoofReplacement /></PageWrapper>} />
          <Route path="/services/skylights-ventilation" element={<PageWrapper><SkylightsVentilation /></PageWrapper>} />
          <Route path="/services/new-construction" element={<PageWrapper><NewConstruction /></PageWrapper>} />
          <Route path="/services/emergency-repairs" element={<Navigate to="/services/roof-repairs" replace />} />
          <Route path="/admin/login" element={<PageWrapper><AdminLogin /></PageWrapper>} />
          <Route path="/admin/dashboard" element={
            <ProtectedAdminRoute>
              <PageWrapper><AdminDashboard /></PageWrapper>
            </ProtectedAdminRoute>
          } />
          <Route path="/admin/leads" element={
            <ProtectedAdminRoute>
              <PageWrapper><Leads /></PageWrapper>
            </ProtectedAdminRoute>
          } />
          <Route path="/blog" element={<PageWrapper><Blog /></PageWrapper>} />
          <Route path="/blog/:slug" element={<PageWrapper><BlogPost /></PageWrapper>} />
          <Route path="/blog/category/:slug" element={<PageWrapper><BlogCategory /></PageWrapper>} />
          {/* Location Pages */}
          <Route path="/houston-roofing" element={<PageWrapper><HoustonRoofing /></PageWrapper>} />
          <Route path="/roofing-the-woodlands-tx" element={<PageWrapper><TheWoodlandsRoofing /></PageWrapper>} />
          <Route path="/roofing-kingwood-tx" element={<PageWrapper><KingwoodRoofing /></PageWrapper>} />
          <Route path="/roofing-atascocita-tx" element={<PageWrapper><AtascocitaRoofing /></PageWrapper>} />
          <Route path="/roofing-humble-tx" element={<PageWrapper><HumbleRoofing /></PageWrapper>} />
          <Route path="/roofing-spring-tx" element={<PageWrapper><SpringRoofing /></PageWrapper>} />
          <Route path="/roofing-conroe-tx" element={<PageWrapper><ConroeRoofing /></PageWrapper>} />
          <Route path="/roofing-aldine-tx" element={<PageWrapper><AldineRoofing /></PageWrapper>} />
          <Route path="/roofing-walden-lake-houston-atascocita-tx" element={<PageWrapper><WaldenLakeHouston /></PageWrapper>} />
          <Route path="/admin/blog/posts" element={<ProtectedAdminRoute><PageWrapper><BlogPosts /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/blog/posts/new" element={<ProtectedAdminRoute><PageWrapper><BlogPostEditor /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/blog/posts/:id/edit" element={<ProtectedAdminRoute><PageWrapper><BlogPostEditor /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/blog/preview/:slug" element={<PageWrapper><BlogPostPreview /></PageWrapper>} />
          <Route path="/admin/blog/categories" element={<ProtectedAdminRoute><PageWrapper><Categories /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/blog/tags" element={<ProtectedAdminRoute><PageWrapper><Tags /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/users" element={<ProtectedAdminRoute><PageWrapper><Users /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/images" element={<ProtectedAdminRoute><PageWrapper><Images /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/projects" element={<ProtectedAdminRoute><PageWrapper><Projects /></PageWrapper></ProtectedAdminRoute>} />
          <Route path="/admin/projects/:id" element={<ProtectedAdminRoute><PageWrapper><ProjectEditor /></PageWrapper></ProtectedAdminRoute>} />
          {/* Redirects for old admin URLs */}
          <Route path="/admin/posts" element={<Navigate to="/admin/blog/posts" replace />} />
          <Route path="/admin/posts/new" element={<Navigate to="/admin/blog/posts/new" replace />} />
          <Route path="/admin/posts/:id/edit" element={<Navigate to="/admin/blog/posts/:id/edit" replace />} />
          <Route path="/admin/categories" element={<Navigate to="/admin/blog/categories" replace />} />
          <Route path="/admin/tags" element={<Navigate to="/admin/blog/tags" replace />} />
            <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
          </Routes>
        </Suspense>
      </AnimatePresence>
    </LanguageWrapper>
  );
};

export default AnimatedRoutes;
